import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { Province, District, City } from "@/lib/types"; // Updated import
import { API_BASE_URL } from "@/lib/api/config"; // Import API_BASE_URL from central config

/**
 * Returns a list of all provinces.
 */
export function useProvinces() {
  return useQuery<Province[]>({
    queryKey: ["provinces"],
    queryFn: async () => {
      const response = await axios.get(
        `${API_BASE_URL}/api/locations/provinces`
      );
      // Assuming the API returns an object with a 'data' property containing the array of provinces
      return response.data.data?.provinces || response.data.provinces || [];
    },
    staleTime: 1000 * 60 * 60 * 24, // 24 hours
  });
}

/**
 * Returns a list of cities for a given province ID.
 */
export function useCitiesByProvinceId(
  provinceId: number | undefined,
  options?: any
) {
  return useQuery<City[]>({
    queryKey: ["citiesByProvince", provinceId],
    queryFn: async () => {
      const response = await axios.get(
        `${API_BASE_URL}/api/locations/provinces/${provinceId}/cities` // Assuming this endpoint exists
      );
      // Assuming the API returns an object with a 'data' property containing the array of cities
      return response.data.data?.cities || response.data.cities || [];
    },
    enabled: !!provinceId, // Only run the query if provinceId is available
    staleTime: 1000 * 60 * 60 * 24, // 24 hours
    ...options, // Spread the options here
  });
}

/**
 * Returns a list of all cities.
 */
export function useAllCities(options?: any) {
  // Accept optional options
  return useQuery<City[]>({
    queryKey: ["allCities"],
    queryFn: async () => {
      const response = await axios.get(
        `${API_BASE_URL}/api/locations/cities` // Assuming this endpoint exists
      );
      // Assuming the API returns an object with a 'data' property containing the array of cities
      return response.data.data?.cities || response.data.cities || [];
    },
    staleTime: 1000 * 60 * 60 * 24, // 24 hours
    ...options, // Spread the options here
  });
}

/**
 * Returns a list of all districts.
 */
export function useAllDistricts(options?: any) {
  // Accept optional options
  return useQuery<District[]>({
    queryKey: ["allDistricts"],
    queryFn: async () => {
      const response = await axios.get(
        `${API_BASE_URL}/api/locations/districts` // Assuming this endpoint exists
      );
      // Assuming the API returns an object with a 'data' property containing the array of districts
      return response.data.data?.districts || response.data.districts || [];
    },
    staleTime: 1000 * 60 * 60 * 24, // 24 hours
    ...options, // Spread the options here
  });
}

/**
 * Returns a list of districts for a given province ID.
 */
export function useDistrictsByProvinceId(provinceId: number | undefined) {
  // console.log(
  //   "[useDistrictsByProvinceId] provinceId:",
  //   provinceId,
  //   "enabled:",
  //   !!provinceId
  // );
  return useQuery<District[]>({
    queryKey: ["districts", provinceId],
    queryFn: async () => {
      const response = await axios.get(
        `${API_BASE_URL}/api/locations/provinces/${provinceId}/districts`
      );
      // Assuming the API returns an object with a 'data' property containing the array of districts
      return response.data.data?.districts || response.data.districts || [];
    },
    enabled: !!provinceId, // Only run the query if provinceId is available
    staleTime: 1000 * 60 * 60 * 24, // 24 hours
  });
}

/**
 * Returns a list of cities for a given district ID.
 */
/**
 * Returns a specific district by its ID.
 */
export function useDistrictById(districtId: number | undefined) {
  return useQuery<District | null>({
    queryKey: ["district", districtId],
    queryFn: async () => {
      if (!districtId) return null;
      const response = await axios.get(
        `${API_BASE_URL}/api/locations/districts/${districtId}`
      );
      return response.data.data?.district || response.data.district || null;
    },
    enabled: !!districtId, // Only run the query if districtId is available
    staleTime: 1000 * 60 * 60 * 24, // 24 hours
  });
}

/**
 * Returns a specific city by its ID.
 */
export function useCityById(cityId: number | undefined) {
  return useQuery<City | null>({
    queryKey: ["city", cityId],
    queryFn: async () => {
      if (!cityId) return null;
      const response = await axios.get(
        `${API_BASE_URL}/api/locations/cities/${cityId}`
      );
      return response.data.data?.city || response.data.city || null;
    },
    enabled: !!cityId, // Only run the query if cityId is available
    staleTime: 1000 * 60 * 60 * 24, // 24 hours
  });
}

export function useCitiesByDistrictId(districtId: number | undefined) {
  return useQuery<City[]>({
    queryKey: ["cities", districtId],
    queryFn: async () => {
      const response = await axios.get(
        `${API_BASE_URL}/api/locations/districts/${districtId}/cities`
      );
      // Assuming the API returns an object with a 'data' property containing the array of cities
      return response.data.data?.cities || response.data.cities || [];
    },
    enabled: !!districtId, // Only run the query if districtId is available
    staleTime: 1000 * 60 * 60 * 24, // 24 hours
  });
}
